/**
 * 요금제 (공통/요금제와결제, 2026-11-02 대표님)
 * - 가입 직후(무료): 무료, 베이직, 프리미엄 셋. 프리미엄에만 "30일 무료 체험, 언제든지 베이직으로 변경 가능"을 강조한다.
 * - 요금제를 쓰는 중: 베이직과 프리미엄 두 개만 깔끔하게, 버튼은 하나.
 *   다음 결제가 프리미엄이면 베이직 칸에 "베이직으로 다운그레이드"(누르면 못 쓰게 되는 것을 보여 주며 설득),
 *   다음 결제가 베이직이면 프리미엄 칸에 "프리미엄으로 업그레이드".
 * - 해지 버튼은 두지 않는다. 자주 묻는 질문에서 웹 해지 화면(/cancel)으로 잇는다.
 * - 실제 카드 등록은 출시 직전. 시험용 서버에서만 "시험용 시작"과 "시험용 상태 바꾸기"를 보여 준다.
 */
import { useEffect, useState } from 'react';
import { api, type Who } from '../api.ts';
import { Button, Link, Message, Page } from '../ui.tsx';

export interface PlanInfo {
  state: { plan: string; service_status: string; plan_next: string | null; trial_until: string | null; billing_day: number | null };
  active: boolean;
  plans: { plan: string; monthly_price: number; trial_days?: number; highlight?: string }[];
  basic_loses: string[];
  test_billing: boolean;
}

const won = (n: number) => `${n.toLocaleString('ko-KR')}원`;

export function Plan(props: { who: Who; go: (p: string) => void; refresh: () => Promise<void> }) {
  const [info, setInfo] = useState<PlanInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [persuade, setPersuade] = useState(false);
  const owner = props.who.role === '소장님';

  async function load() {
    const r = await api<PlanInfo>('GET', '/api/plan');
    if (r.ok) setInfo(r);
    else setError(r.message);
  }

  useEffect(() => {
    void load();
  }, []);

  async function after(r: { ok: boolean; message?: string }) {
    setError(r.ok ? null : (r.message ?? null));
    await props.refresh();
    await load();
  }

  async function next(plan_next: '베이직' | '프리미엄') {
    setPersuade(false);
    await after(await api('POST', '/api/plan/next', { plan_next }));
  }

  if (!info) return <Page title="요금제"><Message text={error} /></Page>;
  const s = info.state;
  const going = s.plan_next ?? s.plan;
  const shown = info.active ? info.plans.filter((p) => p.plan !== '무료') : info.plans;

  return (
    <Page title="요금제">
      <p>지금: {s.plan} ({s.service_status}){s.trial_until ? `, 체험 끝나는 날 ${s.trial_until}` : ''}{s.plan_next ? `, 다음 결제 ${s.plan_next}` : ''}</p>
      {shown.map((p) => (
        <div key={p.plan} style={{ border: p.highlight ? '3px solid #1f4ea8' : '1px solid #999', borderRadius: 8, padding: 16, margin: '0 0 12px' }}>
          <strong style={{ fontSize: 24 }}>{p.plan}</strong>
          <span style={{ marginLeft: 12 }}>{p.monthly_price === 0 ? '0원' : `월 ${won(p.monthly_price)} (부가세 별도)`}</span>
          {p.highlight && <p style={{ fontWeight: 700, color: '#1f4ea8' }}>{p.highlight}</p>}
          {owner && !info.active && p.plan !== '무료' && (
            info.test_billing
              ? <Button primary={p.plan === '프리미엄'} onClick={() => void api('POST', '/api/plan/test-start', { plan: p.plan }).then(after)}>시험용 시작</Button>
              : <p>카드 등록은 아직 준비 중이에요.</p>
          )}
          {owner && info.active && p.plan === '베이직' && going === '프리미엄' && (
            <Button onClick={() => setPersuade(true)}>베이직으로 다운그레이드</Button>
          )}
          {owner && info.active && p.plan === '프리미엄' && going === '베이직' && (
            <Button primary onClick={() => void next('프리미엄')}>프리미엄으로 업그레이드</Button>
          )}
        </div>
      ))}
      {persuade && (
        <div role="dialog" style={{ border: '2px solid #b00020', borderRadius: 8, padding: 16, margin: '16px 0' }}>
          <p style={{ fontWeight: 700 }}>베이직에서는 이것들을 쓸 수 없어요.</p>
          <ul>{info.basic_loses.map((l) => <li key={l}>{l}</li>)}</ul>
          <Button primary onClick={() => setPersuade(false)}>프리미엄 그대로 쓰기</Button>
          <Button onClick={() => void next('베이직')}>그래도 베이직으로</Button>
        </div>
      )}
      {owner && info.test_billing && (
        <p>
          시험용 상태 바꾸기:{' '}
          {['체험 중', '이용 중', '결제 실패', '이용 막힘', '해지됨'].map((st) => (
            <Button key={st} onClick={() => void api('POST', '/api/plan/test-status', { service_status: st }).then(after)}>{st}</Button>
          ))}
        </p>
      )}
      {!owner && <p>요금제는 소장님 계정만 바꿀 수 있어요.</p>}
      <Message text={error} />
      <p>
        {info.active && <Link to="/" go={props.go}>처음 화면</Link>}
        <Link to="/account" go={props.go}>내 계정</Link>
      </p>
    </Page>
  );
}
